/*
 * "No camera? Sign in from the app instead" — the Okta Verify way.
 *
 * The organization address is the host this console is served from: it is
 * what the app opens (/authenticator/signin) and what the user already types
 * to reach AuthNull. Shown on every invite surface so a user who cannot scan
 * the QR still has a way in. When the invite carries an activation code it is
 * shown too (the app's "Enter activation code").
 */
import { useState } from "react";
import { Check, Copy } from "lucide-react";
import { AN_COLORS, AN_FONT, AN_MONO } from "../../design-system/tokens";

export const consoleAddress = () => window.location.host;

export default function OrgAddressHint({ activationCode, consoleHost, compact = false }) {
  const [copied, setCopied] = useState(false);
  // The server's own answer wins (invites name it); this console's host is the
  // fallback for older servers and for the public /enroll page.
  const host = consoleHost || consoleAddress();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(host);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) { /* clipboard blocked: the address is still on screen */ }
  };

  return (
    <div style={{
      marginTop: compact ? 12 : 16, padding: "12px 14px", fontFamily: AN_FONT,
      border: `1px solid ${AN_COLORS.line}`, borderRadius: 10, background: "var(--an-surface)",
    }}>
      <div style={{ fontSize: 12.5, color: AN_COLORS.ink2, lineHeight: 1.5 }}>
        Can't scan? In the AuthNull Authenticator choose <strong style={{ fontWeight: 600 }}>Add account → Sign in to your organization</strong> and enter:
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
        <span style={{
          fontFamily: AN_MONO, fontSize: 13.5, color: AN_COLORS.ink, padding: "4px 8px",
          background: AN_COLORS.bg, border: `1px solid ${AN_COLORS.line}`, borderRadius: 6,
          wordBreak: "break-all",
        }}>{host}</span>
        <button type="button" onClick={copy} style={{
          display: "inline-flex", alignItems: "center", gap: 5, border: "none", background: "transparent",
          padding: 0, cursor: "pointer", color: AN_COLORS.primary, fontSize: 12.5, fontFamily: AN_FONT,
        }}>
          {copied ? <Check size={13} color={AN_COLORS.success} /> : <Copy size={13} />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      {activationCode && (
        <div style={{ marginTop: 10, fontSize: 12.5, color: AN_COLORS.ink2, lineHeight: 1.5 }}>
          Or choose <strong style={{ fontWeight: 600 }}>Enter activation code</strong> and type{" "}
          <span style={{ fontFamily: AN_MONO, fontSize: 14, letterSpacing: 2, color: AN_COLORS.ink }}>{activationCode}</span>.
        </div>
      )}
    </div>
  );
}
